import { inject, Injectable } from "@angular/core";
import { TitleCasePipe } from "@angular/common";
import { subMonths } from "date-fns";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { BudgetStoreService } from "@bitwarden/web-vault/app/services/store/budget/budget.store.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { BudgetUtils } from "@bitwarden/web-vault/app/models/view/budget/budget.utils";
import { BudgetView } from "@bitwarden/web-vault/app/models/view/budget/budget.view";
import { DefaultBudgetStoreModel } from "@bitwarden/web-vault/app/models/store/budget.store.model";
import { CategoryView } from "@bitwarden/web-vault/app/models/view/category/category.view";
import { CategorySourceEnum } from "@bitwarden/web-vault/app/models/types/category.types";
import { TransactionAmountsTime } from "@bitwarden/web-vault/app/models/types/budget.types";

export interface SuggestedBudget {
  /** One category, or several sharing a taxonomy domain merged into one suggestion. */
  categories: CategoryView[];
  /** Editable default name; the user can rename it before the budget is created. */
  name: string;
  monthlyAmount: number;
}

const LOOKBACK_MONTHS = 12;

// Below this many distinct activity months, a bucket is a burst (e.g. two purchases 36 days
// apart) rather than a recurring cost, and must be dropped instead of averaged.
const MIN_ACTIVITY_MONTHS = 3;

// Average month length, for converting an elapsed span into a fractional month count.
const MS_PER_MONTH = (365.25 / 12) * 24 * 60 * 60 * 1000;

// Per-category income guard — see isIncomeNamedCategory.
const INCOME_KEYWORDS = ["salary", "wage", "payroll", "paycheck", "income", "dividend"];

// Some enrichment sources (e.g. Fiskil) name categories after a taxonomy code, e.g.
// "FOOD_AND_DRINK_COFFEE" — everything before the last segment is a shared "domain", and
// categories sharing one are merged into a single suggestion. Re-derived from `category.name`
// since fiskil.sync.ts collapses the primary/secondary category into one field before a
// CategoryView exists, so the structured domain doesn't survive elsewhere. taxonomyDomain logs
// if this list drifts out of date with the enrichment source.
const TAXONOMY_DOMAIN_PREFIXES = [
  "GOVERNMENT_AND_NON_PROFIT",
  "GENERAL_MERCHANDISE",
  "RENT_AND_UTILITIES",
  "HOME_IMPROVEMENT",
  "GENERAL_SERVICES",
  "TRANSPORTATION",
  "FOOD_AND_DRINK",
  "PERSONAL_CARE",
  "ENTERTAINMENT",
  "LOAN_PAYMENTS",
  "TRANSFER_IN",
  "TRANSFER_OUT",
  "MERCHANDISE",
  "BANK_FEES",
  "MEDICAL",
  "INCOME",
  "TRAVEL",
];

// Money moving to/from the user's own accounts, not spend. TRANSFER_OUT is net negative, so
// unlike INCOME it isn't dropped by the net-flow check below and needs an explicit skip.
const NON_SPEND_DOMAINS = new Set(["TRANSFER_IN", "TRANSFER_OUT"]);

interface CategoryBucket {
  domain: string | null;
  categories: CategoryView[];
}

/**
 * Computes and creates one monthly budget per category (or per shared taxonomy domain, see
 * TAXONOMY_DOMAIN_PREFIXES) sized to its own last LOOKBACK_MONTHS of activity. Categories that
 * already have a budget, have no expense history at all, or look like income are skipped.
 * Uncategorized transactions are not touched — only categories the user already has are
 * considered.
 */
@Injectable({ providedIn: "root" })
export class AutoBudgetService {
  private budgetStoreService = inject(BudgetStoreService);
  private userStoreService = inject(UserStoreService);
  private log = inject(LogService);
  private titleCasePipe = new TitleCasePipe();

  /** Null means the store hasn't finished loading yet (see BudgetStoreService.storeStatus) —
   *  treat as "not ready", distinct from "no suggestions". */
  computeSuggestedBudgets(nowMs: number = Date.now()): SuggestedBudget[] | null {
    if (this.budgetStoreService.storeStatus === "Initialise") {
      return null;
    }

    const categories = this.userStoreService.categories.categoriesWithoutDefault();
    const transactionsByCategory = this.budgetStoreService.budgetTransactions.sortedCollection();
    const existingBudgets = this.budgetStoreService.budget.budgetViews() ?? [];

    const alreadyBudgetedCategoryIds = new Set(
      existingBudgets.flatMap((budget) => BudgetUtils.getCategoryIdsInBudget(budget)),
    );

    const suggestions: SuggestedBudget[] = [];
    for (const bucket of this.bucketCategories(categories)) {
      if (bucket.domain && NON_SPEND_DOMAINS.has(bucket.domain)) {
        continue;
      }

      const unbudgeted = bucket.categories.filter((c) => !alreadyBudgetedCategoryIds.has(c.id));
      if (unbudgeted.length === 0) {
        continue;
      }

      // Drop only the individual categories whose own name reads as income — a single
      // income-flavored category sharing a taxonomy domain with genuine expense categories
      // shouldn't suppress the whole merged suggestion.
      const candidates = unbudgeted.filter((c) => !this.isIncomeNamedCategory(c));
      if (candidates.length === 0) {
        continue;
      }

      const allAmounts = candidates.flatMap((c) => transactionsByCategory?.get(c.id) ?? []);
      if (allAmounts.length === 0) {
        continue;
      }

      // Anchor on the bucket's own last activity (capped at now), not always "today", so a
      // category that's gone quiet is still sized from its real history instead of being
      // dropped for having nothing in the last calendar LOOKBACK_MONTHS.
      const anchorMs = Math.min(this.latestTimestamp(allAmounts), nowMs);
      const windowStart = this.monthsBefore(anchorMs, LOOKBACK_MONTHS);
      const amountsInWindow = allAmounts.filter(
        (t) => t.timeStamp >= windowStart && t.timeStamp <= anchorMs,
      );
      if (amountsInWindow.length === 0) {
        continue;
      }

      const { earliestMs, netSum, activityMonths, activityDays } =
        this.summarizeWindow(amountsInWindow);

      // A burst of activity (e.g. one $5,000 purchase) isn't a recurring cost.
      if (activityMonths < MIN_ACTIVITY_MONTHS) {
        continue;
      }

      // Average over the gaps between activity days (activityDays - 1), not the raw span — e.g.
      // 12 monthly payments span only ~11 months end-to-end, so dividing by the span alone
      // overstates the true monthly amount by ~9%. Counting days rather than months keeps this
      // correct for weekly/daily spend too. No LOOKBACK_MONTHS cap: the window filter already
      // bounds the span, and capping would overstate bills with a payment at both window edges.
      // MIN_ACTIVITY_MONTHS >= 3 guarantees activityDays >= 3, so the denominator stays >= 2.
      const activitySpanMs = anchorMs - earliestMs;
      const windowMonths = (activitySpanMs / MS_PER_MONTH) * (activityDays / (activityDays - 1));

      // Net signed sum agrees with how BudgetUtils.calculateCurrentIntervalSpend measures spend
      // (`spent = Math.max(-actual, 0)`); a net-positive bucket yields a negative monthlyAmount,
      // caught by the guard below. Round before that check so a sub-cent average doesn't pass
      // `> 0` and then round down to a useless $0.00 suggestion.
      const monthlyAmount = Math.round((-netSum / windowMonths) * 100) / 100;
      if (monthlyAmount <= 0) {
        continue;
      }

      suggestions.push({
        categories: candidates,
        name: this.suggestedName(bucket.domain, candidates),
        monthlyAmount,
      });
    }

    return suggestions;
  }

  /** Single vault write and single store trigger for all suggestions (saveToVault), not one
   *  `save` per suggestion — each `save` would serialize behind DaliService's runExclusive anyway
   *  and fire its own full budgetTransactions regeneration. All-or-nothing; saveToVault recovers
   *  the committed subset on a partial-write error. */
  async createBudgets(suggestions: SuggestedBudget[]): Promise<number> {
    if (suggestions.length === 0) {
      return 0;
    }

    const coreCurrency = this.userStoreService.preferences.preferenceView()?.coreCurrency ?? null;
    const views = suggestions.map(
      (suggestion) =>
        new BudgetView({
          ...DefaultBudgetStoreModel,
          nm: suggestion.name,
          am: suggestion.monthlyAmount,
          cur: coreCurrency,
          ctId: suggestion.categories.map((category) => category.id),
          id: crypto.randomUUID(),
          dc: new Date().toISOString(),
          dm: new Date().toISOString(),
          vid: null,
        }),
    );

    await this.budgetStoreService.budget.saveToVault(views, true);
    return views.length;
  }

  /** Groups categories sharing a taxonomy domain into one bucket; categories with no matching
   *  domain prefix each get their own standalone bucket. */
  private bucketCategories(categories: CategoryView[]): CategoryBucket[] {
    const buckets = new Map<string, CategoryBucket>();
    for (const category of categories) {
      const domain = this.taxonomyDomain(category);
      const key = domain ?? `single:${category.id}`;
      if (!buckets.has(key)) {
        buckets.set(key, { domain, categories: [] });
      }
      buckets.get(key).categories.push(category);
    }
    return Array.from(buckets.values());
  }

  /** True for a raw enrichment taxonomy code name, not something the user typed — gated on
   *  provenance first so a manually-named ALL_CAPS category is never mistaken for one. */
  private isTaxonomyCodeCategory(category: CategoryView): boolean {
    return (
      category.source !== CategorySourceEnum.manual && /^[A-Z0-9_]{3,}$/.test(category.name ?? "")
    );
  }

  /** Known taxonomy domain the category's name starts with, or null if it doesn't match one. */
  private taxonomyDomain(category: CategoryView): string | null {
    if (!this.isTaxonomyCodeCategory(category)) {
      return null;
    }
    for (const prefix of TAXONOMY_DOMAIN_PREFIXES) {
      if (category.name === prefix || category.name.startsWith(`${prefix}_`)) {
        return prefix;
      }
    }
    // Taxonomy-shaped name but no known domain matched — TAXONOMY_DOMAIN_PREFIXES is likely stale.
    this.log.warning(
      `[AutoBudgetService]: category "${category.name}" looks like a taxonomy code but matched no known TAXONOMY_DOMAIN_PREFIXES entry`,
    );
    return null;
  }

  /** The shared domain name when more than one category is actually merged, else the single
   *  remaining category's own name. Taxonomy codes are prettified into a title-cased label. */
  private suggestedName(domain: string | null, categories: CategoryView[]): string {
    if (domain && categories.length > 1) {
      return this.prettifyTaxonomyCode(domain);
    }
    const category = categories[0];
    return this.isTaxonomyCodeCategory(category)
      ? this.prettifyTaxonomyCode(category.name)
      : (category.name ?? "");
  }

  /** "FOOD_AND_DRINK_COFFEE" -> "Food And Drink Coffee". */
  private prettifyTaxonomyCode(code: string): string {
    return this.titleCasePipe.transform(code.replace(/_/g, " "));
  }

  /** Per-category income guard, checked so one income-flavored category (e.g. reimbursed spend)
   *  doesn't suppress the rest of a merged taxonomy-domain bucket. Whole-word match so "wage"
   *  matches "wages" but not "sewage"; underscores are normalized to spaces first since `\b`
   *  treats `_` as a word character (else /\bincome\b/ would miss "INCOME_TAX_REFUND"). */
  private isIncomeNamedCategory(category: CategoryView): boolean {
    const name = (category.name ?? "").toLowerCase().replace(/_/g, " ");
    return INCOME_KEYWORDS.some((keyword) => new RegExp(`\\b${keyword}s?\\b`).test(name));
  }

  /** Latest timestamp in a batch of transaction amounts, in one pass. */
  private latestTimestamp(amounts: TransactionAmountsTime[]): number {
    let latest = -Infinity;
    for (const amount of amounts) {
      if (amount.timeStamp > latest) {
        latest = amount.timeStamp;
      }
    }
    return latest;
  }

  /** Earliest timestamp, net signed sum, and distinct activity months/days of a window, in one pass. */
  private summarizeWindow(amounts: TransactionAmountsTime[]): {
    earliestMs: number;
    netSum: number;
    activityMonths: number;
    activityDays: number;
  } {
    let earliestMs = Infinity;
    let netSum = 0;
    const monthsTouched = new Set<string>();
    const daysTouched = new Set<string>();
    for (const amount of amounts) {
      if (amount.timeStamp < earliestMs) {
        earliestMs = amount.timeStamp;
      }
      netSum += amount.amount;
      const date = new Date(amount.timeStamp);
      monthsTouched.add(`${date.getFullYear()}-${date.getMonth()}`);
      daysTouched.add(`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`);
    }
    return {
      earliestMs,
      netSum,
      activityMonths: monthsTouched.size,
      activityDays: daysTouched.size,
    };
  }

  private monthsBefore(referenceMs: number, months: number): number {
    return subMonths(new Date(referenceMs), months).getTime();
  }
}
